/**
 * Section-backdrop scene refactor — quarantine registry.
 *
 * Quarantined features stay in the codebase but are hidden from the inspector
 * and/or bypassed at runtime. Flip a flag to false to restore without a git revert.
 *
 * Work one flag at a time; verify in the browser after each change.
 */

export type SectionBackdropQuarantineId =
  /** 3D floor: reflectionForegroundFade / reflectionDepthFade miswired to horizon feather on section backdrop. */
  | 'miswiredFloorFadeControls'
  /** 3D floor: reflectionVisibility duplicates floorReflectivity on section backdrop. */
  | 'duplicateReflectionVisibility'
  /** 3D floor: near-base sharpness + viewer-side softness — no meaningful section-backdrop effect. */
  | 'unusedFloorSharpnessControls'
  /** CSS 2D flipped symbol art on the floor zone — not the WebGL mirror. */
  | 'cssSymbolFloorReflection'
  /** CSS 2D blurred wall texture mirrored on the floor zone — not the WebGL mirror. */
  | 'cssWallArtFloorReflection'
  /** CSS floor-zone spill layers below WebGL — horizon band, tail, sheen, wall-light spill, base fill. */
  | 'cssFloorSpillLayers'
  /** WebGL mirror edge nudge — duplicates Scene horizon Y and can expose dark CSS under the canvas. */
  | 'reflectionHorizonScreenShift'
  /** Separate symbol vignette mask — symbols stayed bright outside the wall-light pool while texture dimmed. */
  | 'unifiedWallLighting';

export type SectionBackdropQuarantineEntry = {
  id: SectionBackdropQuarantineId;
  label: string;
  reason: string;
  /** Inspector-only, runtime-only, or both */
  scope: 'inspector' | 'runtime' | 'both';
};

/** Set true to quarantine. Set false to restore that feature. */
export const SECTION_BACKDROP_QUARANTINE: Record<SectionBackdropQuarantineId, boolean> = {
  miswiredFloorFadeControls: true,
  duplicateReflectionVisibility: true,
  unusedFloorSharpnessControls: true,
  cssSymbolFloorReflection: true,
  cssWallArtFloorReflection: true,
  cssFloorSpillLayers: true,
  reflectionHorizonScreenShift: true,
  unifiedWallLighting: true,
};

export const SECTION_BACKDROP_QUARANTINE_CATALOG: SectionBackdropQuarantineEntry[] = [
  {
    id: 'miswiredFloorFadeControls',
    label: 'Miswired floor fade sliders (3D floor overrides)',
    reason:
      'On the holding section backdrop, “Reflection fade toward viewer” and “Reflection depth fade” adjust the horizon cut line, not the labelled front/back reflection fade. Quarantined until reimplemented honestly.',
    scope: 'both',
  },
  {
    id: 'duplicateReflectionVisibility',
    label: 'Reflection visibility slider (3D floor overrides)',
    reason:
      'On the section backdrop, reflection strength is already driven by Floor reflectivity (material mix + output opacity). This slider duplicated that effect.',
    scope: 'both',
  },
  {
    id: 'unusedFloorSharpnessControls',
    label: 'Sharpness at vase base & softness toward viewer (3D floor overrides)',
    reason:
      'On the section backdrop, viewer-side softness is already forced off and near-base sharpness does not map to a visible control path (non-hero uses mapSectionBackdropFloorReflectivity only). Hidden until reimplemented for this scene type.',
    scope: 'both',
  },
  {
    id: 'cssSymbolFloorReflection',
    label: 'Symbol floor reflection (CSS)',
    reason:
      'Fake 2D mirror under the canvas — does not appear in the WebGL floor reflector. Quarantined pending real backdrop-plane reflections.',
    scope: 'both',
  },
  {
    id: 'cssWallArtFloorReflection',
    label: 'Wall art floor reflection (CSS)',
    reason:
      'Fake 2D blurred plaster texture on the floor zone — separate from the real WebGL mirror and not visible to MeshReflectorMaterial. Quarantined pending WebGL wall backdrop plane.',
    scope: 'both',
  },
  {
    id: 'cssFloorSpillLayers',
    label: 'CSS floor spill layers',
    reason:
      'Horizon colour band, colourway tail, backlight floor sheen spill, wall-light floor spill, and floor base fill — 2D layers under the WebGL canvas that do not participate in the real mirror and can fight the reflector. Quarantined pending WebGL wall backdrop plane.',
    scope: 'both',
  },
  {
    id: 'reflectionHorizonScreenShift',
    label: 'Reflection edge vs scene horizon (WebGL nudge)',
    reason:
      'Fine-tune slider that moved the WebGL mirror fade independently of Scene horizon Y — caused misalignment and black bands when shifted. WebGL edge now follows Scene horizon Y only (with fixed calibration).',
    scope: 'both',
  },
  {
    id: 'unifiedWallLighting',
    label: 'Separate symbol fade/mask',
    reason:
      'Symbols used a symbolVignette radial mask while wall texture used wallLightRevealMask — they did not dim together under wall lighting. Unified mode applies one radial falloff from the wall-light source for both.',
    scope: 'both',
  },
];

/** Scene layer toggles hidden while their quarantine flag is true. */
const SCENE_LAYER_TOGGLE_QUARANTINE: Partial<Record<string, SectionBackdropQuarantineId>> = {
  sectionFloorArtReflectionEnabled: 'cssWallArtFloorReflection',
  sectionSceneFloorBaseFillEnabled: 'cssFloorSpillLayers',
  sectionFloorSpillHorizonBandEnabled: 'cssFloorSpillLayers',
  sectionColourwayFloorTailEnabled: 'cssFloorSpillLayers',
  sectionBacklightFloorSheenEnabled: 'cssFloorSpillLayers',
  sectionWallLightFloorSpillEnabled: 'cssFloorSpillLayers',
};

export function isSectionBackdropQuarantined(id: SectionBackdropQuarantineId): boolean {
  return SECTION_BACKDROP_QUARANTINE[id] === true;
}

export function isSceneLayerToggleQuarantined(layerKey: string): boolean {
  const id = SCENE_LAYER_TOGGLE_QUARANTINE[layerKey];
  return id != null && isSectionBackdropQuarantined(id);
}

/** Frozen defaults used when miswiredFloorFadeControls is quarantined (matches holding-page defaults). */
export const QUARANTINED_SECTION_FLOOR_FADE_DEFAULTS = {
  reflectionForegroundFade: 0.42,
  reflectionDepthFade: 0.66,
} as const;

/** When duplicateReflectionVisibility is quarantined, floorReflectivity is the sole strength control. */
export const QUARANTINED_SECTION_REFLECTION_VISIBILITY = 1;

/** Frozen when unusedFloorSharpnessControls is quarantined (matches holding-page defaults). */
export const QUARANTINED_SECTION_FLOOR_SHARPNESS_DEFAULTS = {
  reflectionNearBaseSharpness: 0.62,
  reflectionViewerSideSoftness: 0,
} as const;

/** When reflectionHorizonScreenShift is quarantined, WebGL horizon follows Scene horizon Y + fixed calibration only. */
export const QUARANTINED_REFLECTION_HORIZON_SCREEN_SHIFT = 0;
